import { useEffect, useMemo, useState, Fragment } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { Card, Table, Button, Form, Alert, Spinner, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTriangleExclamation,
  faArrowDown,
  faArrowUp,
  faChevronDown,
  faChevronUp,
  faObjectGroup,
  faScissors,
  faCheck
} from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
// What is on the machine now, and "did somebody actually type a length".
// Shared with Execution History so the two cannot disagree about the run.
import {
  buildLiveDrumRows,
  liveDrumPercent,
  typedLength
} from '../../helpers/liveMachineDrums.js';

import ImpactPreview from '../../components/machines/ImpactPreview';
import DrumSurgeryModal from '../../components/machines/DrumSurgeryModal';

import { http } from '../../features/jobs/api/http';
import { fetchMachineExecutionLogs } from '../../features/execution-logs/api/executionLogsApi';
import { fingerprintExecutions } from '../../helpers/diameterFingerprint';
import { INFLUX_API } from '../../config/api';

// DRUM LEDGER — a machine's finished drums as ONE continuous run, across days.
//
// A machine does not produce drums; it produces metres, and the drums are the
// boundaries somebody put in. When a drum change goes undetected the boundary
// is missing, and from there every later boundary is in the wrong place too —
// so every execution after it is paired with the wrong physical bobbin.
//
// Execution History is per day and per product, which is the wrong shape for
// that: the run that broke on 2026-09-15 crossed midnight and three products.
// Here the range is whatever you choose, the rows are in the order the machine
// worked, and the repair is what it physically was — moving a boundary.
//
// You type ONE length, on the drum where the mistake began. "Shift below" then
// proposes that every drum after it takes the one before it. Every resulting
// number is shown before anything is written, and any of them can be overridden.
// Nothing here decides: only the operator knows what was on the machine.
const fmt2 = v => (v == null ? '-' : Number(v).toFixed(2));
const stamp = ts => (ts ? dayjs(Number(ts)).format('DD MMM HH:mm') : '-');

const DrumLedger = () => {
  const { machineId = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // WHICH DRUMS the repair is allowed to move, as `jobId::tokenId` keys.
  //
  // Every drum in the range is still listed, selected or not — they are
  // boundaries on ONE continuous ruler, and hiding the ones between two chosen
  // drums would make the arithmetic of a shift silently wrong. Selection does
  // not hide anything; it decides what may MOVE (see `call`, which pins the rest
  // to what they already hold).
  //
  // ── it used to be a set of jobIds ─────────────────────────────────────────
  //
  // Picking a product picked every drum of it, which is the wrong grain for the
  // repair. Asked for 2026-11-02 on an L75 run of ten MYF 1x2.5 drums:
  //
  //   *"am cate 10 executii aprox, dar daca eu vreau sa selectez doar 2 din cele
  //   10, putem sa facem asta mai flexibila sa fie token based?"*
  //
  // Everything downstream was already per drum — `targets` is keyed
  // `jobId::tokenId` and pinning sends an unselected drum as its own target — so
  // only the selection itself was coarse. The server needs no change at all.
  const [picked, setPicked] = useState(
    () => new Set((location.state?.drumKeys || []).map(String))
  );
  // A PRODUCT chosen in Execution History still means "wherever it appears in
  // this range", because a run that slipped rarely stops at midnight and the
  // chain needs every drum of it free to move. Expanded over the rows as they
  // load, and only while the planner has not taken the selection over.
  const seededJobIds = useMemo(
    () => (location.state?.jobIds || []).map(String),
    [location.state]
  );
  // Nothing to expand means nothing to wait for. Once the rows have been seeded
  // — or the planner has touched a tick — the expansion must never run again:
  // re-seeding would undo the very refinement this screen exists to allow.
  const [seeded, setSeeded] = useState(() => seededJobIds.length === 0);
  const returnTo = location.state?.returnTo || null;

  const [from, setFrom] = useState(
    () => location.state?.from || dayjs().subtract(2, 'day').format('YYYY-MM-DD')
  );
  const [to, setTo] = useState(() => location.state?.to || dayjs().format('YYYY-MM-DD'));
  const [logs, setLogs] = useState([]);
  // How many CORRECTED records the range holds. Not shown as rows — the chain
  // does not touch them — but worth counting out loud, or the run looks short.
  const [correctedHidden, setCorrectedHidden] = useState(0);
  const [profile, setProfile] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  // `jobId::tokenId` → what the operator says that drum really held.
  const [targets, setTargets] = useState({});
  // Where the shift starts, and where it STOPS. A run rarely slipped all the
  // way to the end: on 15 September the L75 was off by one drum from the
  // green-yellow drum to the last 5x10, and what went wrong after that was a
  // different thing entirely. Shifting through it would swap a 4 m share with a
  // 10 300 m one and call it a repair.
  const [shiftFrom, setShiftFrom] = useState(null);
  const [shiftTo, setShiftTo] = useState(null);
  // Which way the metres a drum gives up are allowed to travel.
  //
  // A boundary can be in the wrong place in either direction. Registered too
  // LATE, one drum swallowed the next and the surplus belongs DOWN the run —
  // the original case, and still the default. Registered too EARLY, the drum on
  // the machine is recorded short and its metres landed on the one AFTER it; on
  // the DTB1250, where a LIFT button ends a drum, that is the common one. The
  // 4x1 of 2026-10-18 was credited 15 007 m of its 21 000 and the 5 921 m that
  // finished it sat on the 2x1 behind it, which no amount of flowing down can
  // repair: the drum that has to grow is the one further back.
  const [direction, setDirection] = useState('down');
  const up = direction === 'up';
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState('');
  // ── the end of the run ────────────────────────────────────────────────────
  //
  // The ledger is built from ExecutionLogs, so it stops at the last FINISHED
  // drum — and the product a mis-recorded drum usually belongs to is the one
  // still turning. On by default here, unlike in Execution History: this screen
  // exists to make the record match the floor, and what is on the floor right
  // now is part of that.
  //
  // Read-only, always. The machine owns these lengths (see
  // helpers/liveMachineDrums.js), so they carry no input and no "Becomes" —
  // they are the run's end, and a destination, never a target.
  const [showLive, setShowLive] = useState(true);
  const [live, setLive] = useState([]);
  // What happens to metres the chain cannot place. No default on purpose — the
  // two answers are opposites and only the person who was there knows which:
  // 'drop'    the counter over-counted, that cable was never made
  // 'ownDrum' it WAS made, on a bobbin nobody registered → not the chain's job
  const [leftoverPolicy, setLeftoverPolicy] = useState('');
  // The ledger can only re-state the length of a drum that exists. When the
  // set of drums is itself wrong — one was never wound, or two were really one
  // bobbin — that is a different repair, on the whole run rather than on a row.
  const [surgery, setSurgery] = useState(null); // { jobId, taskId }
  // Rows whose full consequence is open. Collapsed by default — the list is
  // long by design, and a long list on every row is a wall, not a preview.
  const [openImpact, setOpenImpact] = useState(() => new Set());
  const toggleImpact = key =>
    setOpenImpact(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const rangeFrom = dayjs(from).startOf('day').valueOf();
  const rangeTo = dayjs(to).endOf('day').valueOf();

  const load = async () => {
    setErr('');
    setPlan(null);
    setDone('');
    setLoading(true);
    try {
      const data = await fetchMachineExecutionLogs(machineId, {
        dateFrom: rangeFrom,
        dateTo: rangeTo,
        page: 1,
        limit: 500
      });
      // The API sorts newest-first; a run reads in the order it was worked.
      const all = [...(data?.data || [])].sort(
        (a, b) => Number(a.completedAt) - Number(b.completedAt)
      );

      // CORRECTED records are not part of the run.
      //
      // `apply-drum-chain` loads executions with `cancelled: { $ne: true }`, and
      // rightly: a record that was withdrawn — re-queued, split, corrected —
      // must not donate or receive metres, or they are counted twice. This table
      // listed them anyway, so the ruler on screen had a drum in it that the
      // arithmetic did not, and a shift appeared to skip one: on 18 Oct the
      // cancelled MYYM 2x1 sat between the drums being shifted and its
      // neighbours' new lengths looked like they had come from nowhere.
      const rows = all.filter(r => !r?.cancelled);
      setCorrectedHidden(all.length - rows.length);
      setLogs(rows);

      try {
        const res = await fetch(
          `${INFLUX_API}/api/influx/${machineId}/diameter-profile` +
            `?start=${new Date(rangeFrom).toISOString()}&end=${new Date(rangeTo).toISOString()}` +
            `&bucket=machine_data&every=1m`
        );
        const d = res.ok ? await res.json() : null;
        setProfile(Array.isArray(d?.samples) ? d.samples : []);
      } catch {
        setProfile([]); // no profile is no suggestion; the ledger still works
      }
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'Could not load the run.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (machineId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [machineId]);

  useEffect(() => {
    if (!showLive || !machineId) {
      setLive([]);
      return undefined;
    }
    let alive = true;
    const read = async () => {
      try {
        const { data: q } = await http.get(`/api/redis/queues/${machineId}`);
        if (alive) setLive(buildLiveDrumRows(q));
      } catch {
        if (alive) setLive([]); // no queue is no live rows
      }
    };
    read();
    const timer = setInterval(read, 5000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [showLive, machineId]);

  const fingerprint = useMemo(
    () => (profile.length ? fingerprintExecutions(logs, profile) : new Map()),
    [logs, profile]
  );

  const keyOf = log => `${log.jobId}::${log.tokenId}`;
  const planRow = key => plan?.rows?.find(r => `${r.jobId}::${r.tokenId}` === key) || null;

  // Where the shift starts, as a position in the run — so a row can tell whether
  // it is a legal place for the shift to STOP. A stop on the wrong side of the
  // start is refused by the server ("the shift has to end below where it
  // starts"), and a button that 400s is worse than one that is plainly off.
  const shiftIndex = shiftFrom ? logs.findIndex(l => keyOf(l) === shiftFrom) : -1;

  // Which drums an open-ended shift is free to move, and therefore which one it
  // pushes out of the band. With drums picked, that is the last selected one —
  // not the last of the range, which is pinned and will not budge.
  const freeRows = picked.size > 0 ? logs.filter(l => picked.has(keyOf(l))) : logs;
  const orphan = freeRows.length ? freeRows[up ? 0 : freeRows.length - 1] : null;

  // A product chosen upstream means all of its drums in this range — UNIONED
  // with any drums picked individually, because the two selections answer
  // different questions and one must not erase the other.
  useEffect(() => {
    if (seeded || !logs.length) return;
    const keys = logs.filter(l => seededJobIds.includes(String(l.jobId))).map(keyOf);
    setSeeded(true);
    if (!keys.length) return;
    setPicked(prev => new Set([...prev, ...keys]));
  }, [seeded, seededJobIds, logs]);

  const editPicked = fn => {
    setPlan(null);
    setDone('');
    setSeeded(true);
    setPicked(fn);
  };

  const togglePick = key =>
    editPicked(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  /** Every drum of one product, in one press — the old grain, on demand. */
  const pickProduct = jobId => {
    const keys = logs.filter(l => String(l.jobId) === String(jobId)).map(keyOf);
    const allIn = keys.length > 0 && keys.every(k => picked.has(k));
    editPicked(prev => {
      const next = new Set(prev);
      for (const k of keys) {
        if (allIn) next.delete(k);
        else next.add(k);
      }
      return next;
    });
  };

  const clearPicked = () => editPicked(new Set());

  /**
   * One of the two arrows on a row.
   *
   * Each arrow is a direction AND an anchor in a single press: ↓ means "this is
   * the drum that swallowed the next one", ↑ means "this is the drum whose
   * metres belong to the one before it". Both are on every row, because which
   * mistake it was is the one thing the screen cannot work out for itself.
   */
  const clickShift = (dir, key) => {
    setPlan(null);
    setDone('');

    const sameDir = direction === dir;

    // The anchor, pressed again with its own arrow: clear the whole shift.
    if (shiftFrom === key && sameDir) {
      setShiftFrom(null);
      setShiftTo(null);
      return;
    }

    // Nothing anchored yet, or the other arrow pressed: this row becomes the
    // anchor and the chain flows that way. A stop chosen for the old direction
    // is meaningless under the new one, so it goes.
    if (!shiftFrom || !sameDir) {
      setDirection(dir);
      setShiftFrom(key);
      setShiftTo(null);
      // An ANCHOR must be free to move. The arrow says "this is the drum that
      // swallowed the next one" — if it is pinned to the length it already
      // holds, the one drum the planner named is the one that cannot change, and
      // the press looks like it did nothing. So pressing an arrow selects the
      // row. (Only when a selection exists; with nothing picked every drum is
      // already free and adding one would pin all the others.)
      if (picked.size > 0 && !picked.has(key)) {
        editPicked(prev => new Set([...prev, key]));
      }
      return;
    }

    // Anchored elsewhere, same direction: this is where the shift stops.
    setShiftTo(shiftTo === key ? null : key);
  };

  // An anchor that is no longer in the loaded range is not an anchor — the dates
  // moved under it. Left in place it reads as a live shift the plan cannot
  // honour, and `shiftIndex` of -1 would mark every arrow on one side as an
  // illegal stop and switch them all off.
  useEffect(() => {
    if (!shiftFrom || shiftIndex >= 0) return;
    setShiftFrom(null);
    setShiftTo(null);
    setPlan(null);
  }, [shiftFrom, shiftIndex]);

  const setTarget = (key, value) => {
    setPlan(null);
    setDone('');
    setTargets(t => {
      const next = { ...t };
      if (value === '' || value == null) delete next[key];
      else next[key] = value;
      return next;
    });
  };

  const call = async dryRun => {
    setErr('');
    setBusy(true);
    try {
      const numeric = {};
      for (const [k, v] of Object.entries(targets)) {
        const n = Number(String(v).replace(',', '.')); // European decimals
        if (Number.isFinite(n)) numeric[k] = n;
      }

      // A selection BOUNDS the repair.
      //
      // Picking products in Execution History used to only light their rows up,
      // while the chain still ran over the whole range — so a shift anchored on
      // one of them slid every drum below it, whatever product it belonged to.
      // On 18 Oct that took an RVK 3x2.5 correction through two MYYM 4x1 drums
      // and on into a MYYM 2x1 and a MYYM 5x4, telling the 5x4 it held
      // 14 334 m against parents that produced 14 000 — five drums changed when
      // two were meant.
      //
      // Every drum NOT in the selection is pinned to what it already holds, by
      // sending it as its own target. A pinned row keeps its length and passes
      // the carry straight through (`available - keep` is the carry it arrived
      // with), so metres can still travel ACROSS an unselected drum to reach the
      // next selected one — the ruler stays continuous, which is why the rows
      // between are still listed, but only the chosen DRUMS move.
      //
      // Per drum since 2026-11-02. This is the whole of the token-based repair:
      // two of a product's ten drums selected means the other eight are pinned
      // exactly like another product's would be.
      if (picked.size > 0) {
        for (const log of logs) {
          const k = keyOf(log);
          if (numeric[k] !== undefined) continue; // the planner typed this one
          if (picked.has(k)) continue; // chosen: free to move
          const held = Number(log.producedLength);
          if (Number.isFinite(held)) numeric[k] = Math.round(held * 1000) / 1000;
        }
      }

      const { data } = await http.post('/api/redis/jobs/apply-drum-chain', {
        machineId,
        from: rangeFrom,
        to: rangeTo,
        targets: numeric,
        shiftFrom,
        shiftTo,
        direction,
        reason: 'drum boundaries corrected',
        // Only ever sent when the planner has said so out loud. The server
        // refuses a leftover it was not told what to do with.
        ...(leftoverPolicy === 'drop' ? { leftoverPolicy: 'drop' } : {}),
        dryRun
      });
      if (dryRun) setPlan(data);
      else {
        setDone(`${data.changedCount} drum(s) re-stated.`);
        setTargets({});
        setShiftFrom(null);
        setShiftTo(null);
        setLeftoverPolicy('');
        await load();
      }
    } catch (e) {
      const d = e?.response?.data;
      setErr((d?.errors && d.errors.join('; ')) || d?.error || e?.message || 'Failed.');
      // A refusal that came WITH a plan is still a plan — keep it on screen.
      //
      // The leftover question is answered by two radio buttons that only render
      // when there is a plan holding a leftover. Clearing it here hid the answer
      // behind the question: the error said "say which it is" and there was
      // nothing left on the page to say it with.
      setPlan(d?.needsLeftoverDecision && Array.isArray(d?.rows) ? d : null);
    } finally {
      setBusy(false);
    }
  };

  const hasTargets = Object.keys(targets).length > 0;

  return (
    <Card className="mb-9 mt-3">
      <Card.Body>
        <div className="d-flex align-items-start justify-content-between flex-wrap gap-3 mb-3">
          <div>
            <h3 className="mb-1">Drum ledger — {machineId.toUpperCase()}</h3>
            <div className="fs-9 text-700" style={{ maxWidth: 680 }}>
              Every finished drum in the order the machine made them, across days.
              A drum change the machine never registered leaves one execution
              holding two drums — say what this one really held and the rest
              flows to the next. A drum that ended too early is the same mistake
              the other way round: the drum before it is recorded short, so
              switch the metres to{' '}
              <span className="text-info">↑ to the drum before</span> and they
              flow back instead. When the rest is not another drum&apos;s but a
              bobbin of its own that was never recorded,{' '}
              <span className="text-info">rest is its own drum</span> makes it one
              and asks which product it belongs to — a product that is active or
              queued counts.
            </div>
            <div className="mt-2">
              <Form.Check
                type="switch"
                id="ledger-live"
                className="fs-10"
                checked={showLive}
                onChange={e => setShowLive(e.target.checked)}
                label={
                  live.length
                    ? `Bring live data — ${live.length} drum(s) on the machine now, at the end of the run`
                    : 'Bring live data — what is on the machine now'
                }
              />
            </div>
            {picked.size > 0 ? (
              <div className="fs-10 text-700 mt-1">
                <Badge bg="info" className="fs-10 me-2">
                  {picked.size} drum{picked.size === 1 ? '' : 's'} selected
                  {(() => {
                    const jobs = new Set(
                      logs.filter(l => picked.has(keyOf(l))).map(l => String(l.jobId))
                    );
                    return jobs.size > 1 ? ` · ${jobs.size} products` : '';
                  })()}
                </Badge>
                <b>Only these drums can change.</b> Every other one is held at
                what it already holds — metres still travel across them to reach
                the next selected drum, which is why they are all still listed: a
                run is one continuous ruler, and leaving gaps in it would make a
                shift wrong.
                <Button
                  size="sm"
                  variant="link"
                  className="p-0 ms-2 fs-10"
                  onClick={clearPicked}
                >
                  clear the selection
                </Button>
              </div>
            ) : (
              <div className="fs-10 text-700 mt-1">
                Nothing selected — <b>every drum in the range can move.</b> Tick
                the ones the repair is about and the rest are pinned to what they
                already hold. Two of a product&apos;s ten drums is a valid
                selection: the other eight are pinned exactly like another
                product&apos;s would be.
              </div>
            )}
            {correctedHidden > 0 && (
              <div className="fs-10 text-700 mt-1">
                {correctedHidden} CORRECTED record
                {correctedHidden === 1 ? '' : 's'} in this range{' '}
                {correctedHidden === 1 ? 'is' : 'are'} not listed — a withdrawn
                record is not part of the run and the chain does not move metres
                through it.
              </div>
            )}
          </div>
          <Button
            variant="phoenix-secondary"
            size="sm"
            onClick={() => (returnTo ? navigate(returnTo) : navigate(-1))}
          >
            Back
          </Button>
        </div>

        <div className="d-flex align-items-end gap-2 flex-wrap mb-3">
          <Form.Group>
            <Form.Label className="fs-10 mb-1">From</Form.Label>
            <Form.Control size="sm" type="date" value={from} onChange={e => setFrom(e.target.value)} />
          </Form.Group>
          <Form.Group>
            <Form.Label className="fs-10 mb-1">To</Form.Label>
            <Form.Control size="sm" type="date" value={to} onChange={e => setTo(e.target.value)} />
          </Form.Group>
          <Button size="sm" variant="outline-primary" onClick={load} disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : 'Load run'}
          </Button>

          {/* Which way the metres travel. Not a detail of the arithmetic: it is
              the difference between a drum change seen too late and one seen too
              early, and only one of the two can be repaired by each direction. */}
          <Form.Group>
            <Form.Label className="fs-10 mb-1">The metres a drum gives up go</Form.Label>
            <div className="d-flex gap-1">
              {[
                {
                  value: 'down',
                  label: '↓ to the next drum',
                  hint: 'The drum change went unnoticed — this drum swallowed the one after it'
                },
                {
                  value: 'up',
                  label: '↑ to the drum before',
                  hint: 'The drum ended too early — the drum before this one is recorded short and these metres finished it'
                }
              ].map(opt => (
                <Button
                  key={opt.value}
                  size="sm"
                  variant={direction === opt.value ? 'info' : 'outline-secondary'}
                  title={opt.hint}
                  onClick={() => {
                    if (direction === opt.value) return;
                    setDirection(opt.value);
                    // The anchor means "shift everything past this one", and
                    // past points the other way now — a stop validated as below
                    // the start is invalid above it. Cleared rather than
                    // silently reinterpreted.
                    setShiftFrom(null);
                    setShiftTo(null);
                    setPlan(null);
                    setDone('');
                  }}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
            <Form.Text className="fs-10">
              Also set by the ↓ ↑ arrows on a row, which pick the direction and
              the drum the shift starts from in one press.
            </Form.Text>
          </Form.Group>

          {shiftFrom && (
            <Badge bg="info" className="fs-10 align-self-center">
              shifting {up ? 'above' : 'below'} {shiftFrom.split('::')[1]}
              {shiftTo
                ? ` → stops at ${shiftTo.split('::')[1]}`
                : ` → to the ${up ? 'start' : 'end'} of the range`}
              <Button
                variant="link"
                className="p-0 ms-2 fs-10 text-white"
                onClick={() => {
                  setShiftFrom(null);
                  setShiftTo(null);
                  setPlan(null);
                  setLeftoverPolicy('');
                }}
              >
                clear
              </Button>
            </Badge>
          )}
        </div>

        {/* What an open-ended shift is going to cost, said BEFORE it is run.

            Moving every boundary by one drum pushes the drum at the far end out
            of the range — by construction, not by accident — so a shift with no
            stop always ends in "N m would be left with no drum to hold them".
            That question is answerable, but it is the wrong question most of the
            time: moving metres between two drums needs no shift at all. */}
        {shiftFrom && !shiftTo && orphan && (
          <Alert variant="phoenix-warning" className="py-2 fs-10">
            A shift moves every boundary by one drum, so the drum at the{' '}
            {up ? 'start' : 'end'}{' '}
            {picked.size > 0 ? 'of your selection' : 'of the range'} has nothing
            left to pass its metres to:{' '}
            <b>{fmt2(Number(orphan.producedLength) || 0)} m</b> on{' '}
            <b>{orphan.productLabel || orphan.tokenId}</b> will be left over, and
            you will be asked what happened to them.
            <div className="mt-1">
              Press the same arrow on another row to <b>stop the shift there</b>,
              or type what that last drum really held as well — then nothing is
              left over.
            </div>
            <div className="mt-1">
              <b>To move metres between two drums, you do not need a shift.</b>{' '}
              Clear it and just type what each one really held: the drum{' '}
              {up ? 'before' : 'after'} takes the difference, those two rows
              change and nothing else.
            </div>
          </Alert>
        )}

        {err && <Alert variant="danger" className="py-2 fs-9">{err}</Alert>}
        {done && <Alert variant="success" className="py-2 fs-9">{done}</Alert>}
        {plan?.warnings?.length > 0 && (
          <Alert variant="phoenix-warning" className="py-2 fs-9">
            {plan.warnings.map((w, i) => <div key={i}>{w}</div>)}
          </Alert>
        )}

        <Table responsive hover className="phoenix-table border-top border-200 fs-9 mb-0">
          <thead>
            <tr className="text-center">
              <th style={{ width: '3%' }}>
                {/* Per DRUM, not per product. Two of ten is the point. */}
                <FontAwesomeIcon icon={faCheck} title="Which drums may move" />
              </th>
              <th style={{ width: '4%' }}>#</th>
              <th style={{ width: '12%' }}>Window</th>
              <th style={{ width: '17%' }}>Product</th>
              <th style={{ width: '13%' }}>Ø while running</th>
              <th style={{ width: '11%' }}>Recorded</th>
              <th style={{ width: '13%' }}>
                Really held (m)
                <div className="fs-10 fw-normal text-700">not how much to move</div>
              </th>
              <th style={{ width: '13%' }}>Becomes</th>
              <th style={{ width: '15%' }}>New window</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log, i) => {
              const key = keyOf(log);
              const hint = fingerprint.get(String(log._id))?.suggestion || null;
              const read = fingerprint.get(String(log._id));
              const row = planRow(key);
              const isPicked = picked.has(key);
              return (
                <Fragment key={log._id}>
                <tr
                  style={{
                    verticalAlign: 'middle',
                    textAlign: 'center',
                    // The rows you came here for. The ones between them stay
                    // listed — the shift needs the run unbroken — but they do
                    // not compete for attention.
                    borderLeft: isPicked
                      ? '4px solid var(--phoenix-info)'
                      : '4px solid transparent',
                    opacity: picked.size && !isPicked ? 0.55 : 1
                  }}
                >
                  <td>
                    <Form.Check
                      type="checkbox"
                      className="m-0"
                      checked={isPicked}
                      onChange={() => togglePick(key)}
                      title={
                        isPicked
                          ? 'This drum may move. Untick it to pin it to the length it already holds.'
                          : 'Pinned to the length it already holds. Tick it to let the chain move it.'
                      }
                    />
                  </td>
                  <td className="fw-semibold">{i + 1}</td>
                  <td className="fs-10 font-monospace text-800">
                    {stamp(log.startedAt)}<br />{stamp(log.completedAt)}
                  </td>
                  <td>
                    <Link
                      to={`/apps/project-management/project-details/${log.jobId}`}
                      className="fw-semibold text-decoration-none"
                    >
                      {log.productLabel || '-'}
                    </Link>
                    <div className="fs-10 text-700">
                      {log.color || '-'} · <span className="font-monospace">{log.tokenId}</span>
                    </div>
                    <Button
                      size="sm"
                      variant="link"
                      className="p-0 fs-10"
                      onClick={() => pickProduct(log.jobId)}
                      title="Tick every drum of this product in the range — or untick them all if they already are"
                    >
                      all drums of this product
                    </Button>
                    {log.taskId && (
                      <Button
                        size="sm"
                        variant="link"
                        className="p-0 fs-10"
                        onClick={() => setSurgery({ jobId: log.jobId, taskId: log.taskId })}
                        title="Every drum of this run — including ones that were never wound"
                      >
                        <FontAwesomeIcon icon={faObjectGroup} className="me-1" />
                        drums of this run
                      </Button>
                    )}
                    {hint && (
                      <div className="fs-10 text-warning mt-1">
                        <FontAwesomeIcon icon={faTriangleExclamation} className="me-1" />
                        {hint.kind === 'CHANGED_MID_DRUM'
                          ? `Ø changed at ${dayjs(hint.splitAt).format('HH:mm')} — ~${hint.metresAfterEstimate.toLocaleString()} m after`
                          : `made at Ø${hint.dominant}, ${hint.agreeing}/${hint.total} of this product at Ø${hint.expected}`}
                      </div>
                    )}
                  </td>
                  <td className="fs-10 font-monospace text-800">
                    {read?.stretches?.length
                      ? read.stretches.map(s => `Ø${s.diameter} ${s.minutes}m`).join(' → ')
                      : '-'}
                  </td>
                  <td className="font-monospace text-800">{fmt2(log.producedLength)}</td>
                  <td>
                    <div className="d-flex align-items-center gap-1">
                      <Form.Control
                        size="sm"
                        type="text"
                        inputMode="decimal"
                        placeholder={
                          hint?.keepLengthEstimate != null
                            ? String(hint.keepLengthEstimate)
                            : '—'
                        }
                        isInvalid={
                          targets[key] != null &&
                          targets[key] !== '' &&
                          !Number.isFinite(Number(String(targets[key]).replace(',', '.')))
                        }
                        value={targets[key] ?? ''}
                        onChange={e => setTarget(key, e.target.value)}
                        title="What this drum really held — not how much to move"
                      />
                      {/* One click instead of ten, and now in both directions.
                          There used to be a single arrow that followed the
                          toggle above the table, which made the row say "shift"
                          without saying WHICH WAY — the half of the mechanism
                          that was missing. Each arrow is a direction and an
                          anchor in one press; a second press on another row
                          marks where the shift stops. */}
                      {[
                        { dir: 'down', icon: faArrowDown },
                        { dir: 'up', icon: faArrowUp }
                      ].map(({ dir, icon }) => {
                        const isAnchor = shiftFrom === key && direction === dir;
                        const isStop = shiftTo === key && direction === dir;
                        // A stop has to be past the start, in the direction the
                        // metres travel. Anything else the server refuses.
                        const wouldStop =
                          !!shiftFrom &&
                          shiftFrom !== key &&
                          shiftIndex >= 0 &&
                          direction === dir;
                        const badStop =
                          wouldStop &&
                          (dir === 'down' ? i <= shiftIndex : i >= shiftIndex);

                        return (
                          <Button
                            key={dir}
                            size="sm"
                            disabled={badStop}
                            variant={
                              isAnchor ? 'info' : isStop ? 'outline-info' : 'outline-secondary'
                            }
                            title={
                              badStop
                                ? `The shift starts ${dir === 'down' ? 'below' : 'above'} this drum — it cannot also end here`
                                : isAnchor
                                  ? 'Clear the shift'
                                  : wouldStop
                                    ? 'Stop the shift at this drum'
                                    : dir === 'down'
                                      ? 'This drum swallowed the next one — shift the drums below it by one'
                                      : 'This drum ended too early — shift its metres back, and the drums above it by one'
                            }
                            style={{ width: 30, height: 30, padding: 0, flexShrink: 0 }}
                            onClick={() => clickShift(dir, key)}
                          >
                            <FontAwesomeIcon icon={icon} className="fs-10" />
                          </Button>
                        );
                      })}
                    </div>

                    {/* The direction, as you type. The column asks what the drum
                        HELD; the natural thing to reach for is how much to move,
                        and the two are opposite numbers on the same row. Saying
                        the consequence out loud costs nothing and settles it. */}
                    {(() => {
                      // `Number('')` is 0, and 0 is finite — so an untouched
                      // field used to read as "this drum held nothing" and the
                      // row announced that its whole 6 344 m were moving to the
                      // next drum before anybody had typed a thing. An explicit
                      // 0 is still a legitimate answer; only the absence is not.
                      const typed = typedLength(targets[key]);
                      if (!Number.isFinite(typed)) return null;
                      const moves = Number(log.producedLength) - typed;
                      if (Math.abs(moves) < 0.01) {
                        return (
                          <div className="fs-10 text-700 mt-1">unchanged</div>
                        );
                      }
                      return (
                        <div
                          className={`fs-10 mt-1 ${moves > 0 ? 'text-warning' : 'text-danger'}`}
                        >
                          {moves > 0
                            ? up
                              ? `↑ ${fmt2(moves)} m go to the drum before this one`
                              : `↓ ${fmt2(moves)} m go to the next drum`
                            : `+ ${fmt2(-moves)} m more than was recorded here`}
                        </div>
                      );
                    })()}

                    {/* The other answer to "where do the rest of the metres go".
                        The chain hands them to the NEXT drum, which is right
                        when a boundary slipped — and wrong when a whole bobbin
                        was wound unnoticed, because then they are not the next
                        drum's, they are a drum of their own that has no record.
                        That is the case the ledger cannot express: it refuses
                        with "there is a drum here with no execution to hold it",
                        and the planner has nowhere to go.
                        Here is where they go. */}
                    {/* The consequence that was only visible behind "new
                        window", on a screen whose Apply button is right there.
                        The chain re-states a length through the ordinary length
                        correction, and that hands metres BACK to the drums this
                        one was made from — which is right when the metres moved
                        to another drum of the same product, and wrong when a
                        whole bobbin was wound unnoticed: then the cores really
                        did give what they gave.
                        Read off the preview rather than guessed from the phase,
                        so it says what will actually happen. */}
                    {(() => {
                      const derived = Array.isArray(row?.impact?.derived)
                        ? row.impact.derived
                        : [];
                      const upstream = derived.filter(
                        d =>
                          /^tokens\..*\.consumedLength$/.test(String(d.field)) &&
                          !String(d.field).includes(String(log.tokenId))
                      );
                      if (!upstream.length) return null;
                      const given = upstream.reduce(
                        (s, d) => s + (Number(d.from) - Number(d.to)),
                        0
                      );
                      return (
                        <div className="fs-10 mt-1 text-warning">
                          <FontAwesomeIcon
                            icon={faTriangleExclamation}
                            className="me-1"
                          />
                          Applying gives {fmt2(given)} m back across{' '}
                          {upstream.length} upstream drum(s) — the cable this one
                          was made from. If a whole bobbin was wound unnoticed,
                          those metres were really used: use{' '}
                          <span className="text-info">rest is its own drum</span>{' '}
                          instead.
                        </div>
                      );
                    })()}

                    {(() => {
                      const typed = typedLength(targets[key]);
                      const rest = Number(log.producedLength) - typed;
                      if (!Number.isFinite(typed) || rest <= 0.01) return null;
                      return (
                        <Button
                          size="sm"
                          variant="link"
                          className="p-0 mt-1 fs-10 d-block text-info"
                          title={
                            `Make the remaining ${fmt2(rest)} m their own finished drum, ` +
                            `then say which product it belongs to — including one that is ` +
                            `active or queued right now`
                          }
                          onClick={() =>
                            setSurgery({
                              jobId: log.jobId,
                              taskId: log.taskId,
                              initial: {
                                mode: 'split',
                                tokenId: log.tokenId,
                                keepLength: typed
                              }
                            })
                          }
                        >
                          <FontAwesomeIcon icon={faScissors} className="me-1" />
                          rest is its own drum ({fmt2(rest)} m)
                        </Button>
                      );
                    })()}

                    {hint?.keepLengthEstimate != null && targets[key] == null && (
                      <Button
                        size="sm"
                        variant="link"
                        className="p-0 mt-1 fs-10"
                        onClick={() => setTarget(key, String(hint.keepLengthEstimate))}
                        title="From the diameter setpoint — an estimate, check it"
                      >
                        use {fmt2(hint.keepLengthEstimate)}
                      </Button>
                    )}
                  </td>
                  <td
                    className={`font-monospace ${
                      row?.changed ? 'fw-bold text-warning' : 'text-700'
                    }`}
                  >
                    {row ? fmt2(row.keep) : '—'}
                    {row?.changed && (
                      <div className="fs-10 text-700">was {fmt2(row.recorded)}</div>
                    )}
                  </td>
                  <td className="fs-10 font-monospace text-800">
                    {row?.changed ? (
                      <>
                        {stamp(row.window.from)}<br />{stamp(row.window.to)}
                        {/* Every other number this one moves. Not a count —
                            the actual fields, before and after. */}
                        <Button
                          size="sm"
                          variant="link"
                          className="p-0 mt-1 fs-10 d-block mx-auto"
                          onClick={() => toggleImpact(key)}
                        >
                          <FontAwesomeIcon
                            icon={openImpact.has(key) ? faChevronUp : faChevronDown}
                            className="me-1"
                          />
                          {openImpact.has(key) ? 'hide' : 'what else changes'}
                        </Button>
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>

                {row?.changed && openImpact.has(key) && (
                  <tr>
                    <td colSpan={9} className="bg-body-tertiary px-3 py-2 text-start">
                      {/* `row.impact`, not `row`: the impact's `operators` is a
                          before/after summary, while the row's own is the array
                          of spans that gets written. Merging the two lost
                          fourteen executions' operator lists. */}
                      <ImpactPreview row={row.impact} />
                    </td>
                  </tr>
                )}
                </Fragment>
              );
            })}
            {!logs.length && !loading && (
              <tr>
                <td colSpan={9} className="text-center py-5 text-700 fs-9">
                  No finished drums in this range.
                </td>
              </tr>
            )}

            {/* ── the run's end ──
                At the BOTTOM, because this table is chronological — these are
                the newest thing on the machine. Read-only on purpose: the line
                is writing their lengths, so there is no "really held" to type
                and no boundary to move. What they give is the end of the ruler,
                and a product you can name as where a drum above belongs. */}
            {showLive && live.length > 0 && (
              <>
                <tr>
                  <td
                    colSpan={9}
                    className="py-1 px-3 bg-body-tertiary"
                    style={{ borderTop: '2px solid var(--phoenix-primary)' }}
                  >
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <Badge bg="primary" className="fs-10">
                        on the machine now
                      </Badge>
                      <span className="fs-10 text-700">
                        Where the run has got to. Its own length cannot be
                        re-stated — the line is writing it — but the product{' '}
                        <b>can receive a drum</b>: use{' '}
                        <span className="text-info">rest is its own drum</span> on
                        the row that holds its metres and name it there.
                      </span>
                    </div>
                  </td>
                </tr>

                {live.map(row => (
                  <tr
                    key={row.key}
                    style={{
                      verticalAlign: 'middle',
                      borderLeft: '4px solid var(--phoenix-primary)',
                      opacity: 0.92
                    }}
                  >
                    {/* No tick: the line owns this drum's length, so there is no
                        boundary of its own to move. It is a destination. */}
                    <td />
                    <td className="text-center fw-semibold text-primary">now</td>
                    <td className="fs-10 font-monospace text-800">
                      {row.status === 'ACTIVE' ? 'running' : 'queued'}
                    </td>
                    <td>
                      <Link
                        to={`/apps/project-management/project-details/${row.jobId}`}
                        className={`text-decoration-none ${
                          logs.some(
                            l =>
                              String(l.jobId) === String(row.jobId) && picked.has(keyOf(l))
                          )
                            ? 'fw-bold'
                            : 'fw-semibold'
                        }`}
                      >
                        {row.productLabel}
                      </Link>
                      <div className="fs-10 text-700">
                        {row.color || '-'} ·{' '}
                        <span className="font-monospace">{row.tokenId}</span>
                      </div>
                      <div className="fs-10 text-700">
                        {(row.phase || '-').replaceAll('_', ' ')}
                        {row.drum_diameter
                          ? ` · ${row.drum_material || ''} Ø${row.drum_diameter}`
                          : ''}
                      </div>
                    </td>
                    <td className="fs-10 font-monospace text-800">-</td>
                    <td>
                      <div className="font-monospace text-800">
                        {fmt2(row.producedLength)}
                      </div>
                      <div className="fs-10 text-700">
                        of {fmt2(row.plannedLength)} m · {liveDrumPercent(row)}%
                      </div>
                    </td>
                    {/* Not a dead end, which is how "nothing to re-state" read.
                        The drum's length is the line's to write; the PRODUCT is
                        still somewhere a mis-recorded drum can be sent. */}
                    <td className="fs-10 text-700">
                      the line writes this one
                    </td>
                    <td className="fs-10 text-info">
                      can receive a drum ↑
                    </td>
                    <td className="fs-10 text-700">—</td>
                  </tr>
                ))}
              </>
            )}
          </tbody>
        </Table>

        {/* ── metres the chain cannot place ──
            Two opposite situations wearing one face, and this used to answer
            both the same way: a warning line, and the metres were dropped.
            On 2026-10-12 one press of Apply deleted 2 294.605 m of CYYF 5x10
            rope that physically existed and handed 11 475 m of core consumption
            back across five drums. Only the person who was there can tell the
            two apart, so now they have to say. The server refuses a leftover it
            was not told about, so this is not merely a nag. */}
        {plan?.leftover > 0.01 && (
          <Alert variant="phoenix-warning" className="py-2 fs-9 mt-3 mb-0">
            <div className="fw-semibold mb-2">
              <FontAwesomeIcon icon={faTriangleExclamation} className="me-2" />
              {fmt2(plan.leftover)} m have no drum to hold them. Which is it?
            </div>

            <Form.Check
              type="radio"
              name="leftoverPolicy"
              id="leftover-drop"
              className="fs-9"
              checked={leftoverPolicy === 'drop'}
              onChange={() => setLeftoverPolicy('drop')}
              label={
                <>
                  That cable was <b>never made</b> — the counter over-counted
                  <div className="fs-10 text-700">
                    The metres go, and the drums this run was made from get them
                    back. That give-back is correct here: less cable was made, so
                    less was used.
                  </div>
                </>
              }
            />

            <Form.Check
              type="radio"
              name="leftoverPolicy"
              id="leftover-own"
              className="fs-9 mt-2"
              checked={leftoverPolicy === 'ownDrum'}
              onChange={() => setLeftoverPolicy('ownDrum')}
              label={
                <>
                  It <b>was made</b>, on a bobbin nobody registered
                  <div className="fs-10 text-700">
                    Then this is not the chain&apos;s job — it would delete a drum
                    that exists and take cable off cores that really gave it. Use{' '}
                    <span className="text-info fw-semibold">rest is its own drum</span>{' '}
                    on that row instead: the total is kept, the cores are left
                    alone, and the drum goes to the product that really made it.
                  </div>
                </>
              }
            />

            {leftoverPolicy === 'ownDrum' && (
              <div className="fs-10 mt-2 text-900">
                Apply stays off — the chain is the wrong tool for this. The row
                above now carries the link.
              </div>
            )}
          </Alert>
        )}

        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 pt-3">
          <div className="fs-9 text-700">
            {plan ? (
              <>
                <b>{plan.changedCount}</b> drum(s) would change.{' '}
                {plan.leftover > 0.01 && (
                  <span className="text-warning">
                    {fmt2(plan.leftover)} m left over — the chain has nowhere to put them.
                  </span>
                )}
              </>
            ) : (
              'Type what a drum really held, then preview.'
            )}
          </div>
          <div className="d-flex gap-2">
            <Button
              variant="outline-primary"
              disabled={!hasTargets || busy}
              onClick={() => call(true)}
            >
              {busy && !plan ? <Spinner animation="border" size="sm" /> : 'Preview'}
            </Button>
            <Button
              variant="primary"
              // A leftover has to be accounted for before anything is written.
              // 'ownDrum' never enables Apply — it is the answer that says the
              // chain is the wrong tool.
              disabled={
                !plan ||
                !plan.changedCount ||
                busy ||
                (plan.leftover > 0.01 && leftoverPolicy !== 'drop')
              }
              onClick={() => call(false)}
              title={
                !plan
                  ? 'Preview it first'
                  : plan.leftover > 0.01 && leftoverPolicy !== 'drop'
                    ? 'Say what happens to the leftover metres first'
                    : 'Write these boundaries'
              }
            >
              Apply
            </Button>
          </div>
        </div>
      </Card.Body>

      <DrumSurgeryModal
        show={!!surgery}
        onHide={() => setSurgery(null)}
        jobId={surgery?.jobId}
        taskId={surgery?.taskId}
        machineId={machineId}
        initial={surgery?.initial || null}
        // Cancelling and merging retire the very ExecutionLog rows this table
        // is built from, so the ledger has to be re-read or it would keep
        // offering to shift metres between drums that no longer exist.
        onApplied={load}
      />
    </Card>
  );
};

export default DrumLedger;
